import React from 'react';
import styled from 'styled-components';
import RichTextSerializer from '../components/richTextSerializer';
import SEO from '../components/seo';
import Layout from '../components/shared/layout';
import { IContentPage, TPageTable, ILayoutData } from '../interfaces';
import * as sanity from '../sanity-image';
import { fontSize, space } from '../components/styles';
import { Discussion } from '../components/community/Discussion';
import { useI18n } from '../i18n';
import { getMetadataFromPageTable } from '../helpers/get-metadata';
import { Breadcrumbs } from '../components/tailwind/molecules/breadcrumbs';
import { Container } from '../components/tailwind/atoms/container';

const ContentContainer = styled.div`
    margin: 100px 0;
    padding: 0 ${space(2)};
`;
const TagTitle = styled.span`
    ${fontSize.regular};
    color: #737373;
    margin-right: 10px;
    display: block;

    @media (min-width: 730px) {
        display: inline;
    }
`;
const CtaTitle = styled.span`
    ${fontSize.large};

    @media (max-width: 768px) {
        ${fontSize.headinglarge}
    }
`;

interface PageContext {
    pageTable: TPageTable;
    layoutData: ILayoutData;
    content: IContentPage;
}

const ContentPage: React.FC<{
    pageContext: PageContext;
}> = ({ pageContext: { content, layoutData, pageTable } }) => {
    const { t } = useI18n();
    const { language } = layoutData;
    const ogImageString = content.image
        ? sanity.urlFor(content.image).width(1200).height(630).url()
        : '';
    const twitterImageString = content.image
        ? sanity.urlFor(content.image).width(450).height(450).url()
        : '';

    const metadata = getMetadataFromPageTable({
        pageTable: pageTable,
        language: language,
        withDefaultTag: true,
    });

    const categoryPage = content.categoryPage;

    return (
        <Layout pageTable={pageTable} layoutData={layoutData}>
            <SEO
                title={content.title}
                lang={language}
                description={content.description}
                alternates={metadata?.alternates}
                canonicalURL={metadata?.canonicalUrl}
                ogImage={ogImageString}
                twitterImage={twitterImageString}
            />
            <Container>
                <Breadcrumbs
                    className="my-4 md:my-6"
                    language={language}
                    links={[
                        ...(categoryPage
                            ? [
                                  {
                                      url: `/${language}/${categoryPage?.slug?.current}` || '',
                                      title: categoryPage?.title || '',
                                  },
                              ]
                            : []),
                        {
                            url: pageTable[language],
                            title: content.title,
                        },
                    ]}
                />
            </Container>
            {content.image && (
                <picture
                    css={`
                        * {
                            width: 100%;
                        }
                    `}
                >
                    <source
                        media="(min-width: 730px)"
                        srcSet={sanity.srcSet(
                            [[6000], [5000], [4000], [3000], [2000], [1000], [500], [400], [200]],
                            content.image
                        )}
                        sizes="100vw"
                    />
                    <img
                        src={sanity.urlFor(content.image).width(730).height(547).url() || undefined}
                        sizes="100vw"
                        srcSet={sanity.srcSet(
                            [
                                [2190, 1642],
                                [1460, 1095],
                                [730, 547],
                                [340, 255],
                            ],
                            content.image
                        )}
                    />
                </picture>
            )}

            <ContentContainer>
                <div
                    css={`
                        max-width: 800px;
                        margin: 0 auto 64px;
                    `}
                >
                    {content.tag && <TagTitle>{content.tag}</TagTitle>}
                    {content.title && !content.dontRenderTitle && (
                        <CtaTitle>{content.title}</CtaTitle>
                    )}
                </div>
                <RichTextSerializer
                    sizes="(min-width: 990px) 814px, 80vw"
                    productSelectionSizes="(min-width: 2625px) calc((2100px - 48px)/3), (min-width: 1550px) calc((80vw - 48px)/3), (min-width: 1240px) calc((1240px - 48px)/3), (min-width: 1051px) calc((100vw - 48px)/3), 330px"
                    content={content.text}
                    displayOptions={{
                        maxWidth: '800px',
                        maxWidthProductSelection: '1240px',
                        textMaxWidth: '620px',
                    }}
                />
            </ContentContainer>
            {content.enableDiscussion && (
                <div
                    id="discussion"
                    css={`
                        box-sizing: border-box;
                        *,
                        *:before,
                        *:after {
                            box-sizing: inherit;
                        }
                        width: 100%;
                        max-width: 1240px;

                        margin: 0 auto;
                        padding: 0 ${space(3)};

                        @media (min-width: 1550px) {
                            max-width: 2100px;
                            width: 80%;
                        }
                    `}
                >
                    <Discussion
                        textareaLabel={t('Any feedback on this article? Share your thoughts here.')}
                        topic={`content_page_${content.sanityId}`}
                    />
                </div>
            )}
        </Layout>
    );
};

export default ContentPage;
